import React from 'react';
import { useSearchParams } from 'react-router-dom';
import ClickableOpacity from '@hero/ui-kit/inputs/ClickableOpacity';
import SettingsIcon from '@hero/ui-kit/icons/utility/SettingsIcon';
import CheckboxSquare from '@hero/ui-kit/graphics/CheckboxSquare';
import Section from '@hero/ui-kit/layout/Section';
import P from '@hero/ui-kit/typography/P';
import CheckIcon from '@hero/ui-kit/icons/utility/CheckIcon';
import * as Style from './style.module.scss';
import { calcCurrentPage } from '@hero/ui-kit/tables/Pager/usePagination/utils';
import useOutsideClick from '@hero/ui-kit/hooks/useOutsideClick';

export interface ColumnOption {
    id: string;
    label: string;
    isVisible: boolean;
    disabled?: boolean;
}

interface LimitItemsProps {
    limit?: number;
    total_count?: number;
    offset?: number;
    className?: string;
    isLoading?: boolean;
    columnControl?: {
        title?: string;
        options: ColumnOption[];
        onToggle: (id: string) => void;
    };
}

const LimitItems: React.FC<LimitItemsProps> = ({
    limit = 10,
    total_count,
    offset,
    className,
    isLoading = false,
    columnControl
}) => {
    const [_, setSearchParams] = useSearchParams();
    const [showLimitBox, setShowLimitBox] = React.useState(false);
    const [dropdownRef, isOutsideClick] = useOutsideClick(showLimitBox);

    const columnMenu = columnControl && columnControl.options.length > 0 ? columnControl : null;

    React.useEffect(() => {
        if (isOutsideClick) {
            setShowLimitBox(false);
        }
    }, [isOutsideClick]);

    const limits = [
        {
            label: '10 items',
            value: 10,
            isSelected: limit === 10
        },
        {
            label: '25 items',
            value: 25,
            isSelected: limit === 25
        },
        {
            label: '50 items',
            value: 50,
            isSelected: limit === 50
        }
    ];

    const handleLimitChange = (limit: number) => {
        setSearchParams((prev) => {
            prev.set('limit', `${limit}`);
            return prev;
        });
    };

    if (isLoading || !limit || !total_count) {
        return <div className={`${Style.limitItemsWrapper} ${className}`} />;
    }

    return total_count > 0 ? (
        <div className={`${Style.limitItemsWrapper} ${className}`}>
            {`${calcCurrentPage(offset || 0, limit)}-${
                limit > total_count ? total_count : limit
            } of ${total_count}`}
            <div
                className={Style.filterStatus}
                ref={dropdownRef as React.RefObject<HTMLDivElement>}
            >
                <ClickableOpacity
                    className={`${Style.filterStatusToggle} ${
                        showLimitBox ? Style.openLimitBox : ''
                    }`}
                    alt="open table settings"
                    onClick={() => setShowLimitBox((prev) => !prev)}
                >
                    <SettingsIcon size="utility" />
                </ClickableOpacity>
                {showLimitBox && (
                    <Section
                        noDefaultPadding
                        className={`${Style.filterStatusDropdown} ${
                            columnMenu ? Style.filterStatusDropdownWide : ''
                        }`}
                        border={true}
                    >
                        <div className={columnMenu ? Style.dropdownColumns : ''}>
                            <div className={Style.dropdownColumn}>
                                <P strong noDefaultMargin className={Style.headerLabel}>
                                    Show up to
                                </P>
                                {limits.map((limit) => (
                                    <ClickableOpacity
                                        fullWidth
                                        key={limit.label}
                                        alt={`Toggle ${limit.label}`}
                                        className={`${Style.filterStatusItem} ${
                                            limit.isSelected ? Style.selected : ''
                                        }`}
                                        onClick={() => {
                                            handleLimitChange(limit.value);
                                        }}
                                    >
                                        <CheckIcon
                                            size="small"
                                            type="brand"
                                            styles={{
                                                visibility: limit.isSelected ? 'visible' : 'hidden'
                                            }}
                                        />
                                        <P noDefaultMargin>{limit.label}</P>
                                    </ClickableOpacity>
                                ))}
                            </div>
                            {columnMenu && (
                                <>
                                    <div className={Style.dividerVertical} />
                                    <div className={Style.dropdownColumn}>
                                        <P strong noDefaultMargin className={Style.headerLabel}>
                                            {columnMenu.title || 'Columns'}
                                        </P>
                                        <div
                                            className={`${Style.columnsList} ${
                                                columnMenu.options.length > 7
                                                    ? Style.columnsListTwoCol
                                                    : ''
                                            }`}
                                        >
                                            {columnMenu.options.map((option) => (
                                                <ClickableOpacity
                                                    fullWidth
                                                    key={option.id}
                                                    disabled={option.disabled}
                                                    alt={`Toggle ${option.label} column`}
                                                    className={Style.filterStatusItem}
                                                    onClick={() => columnMenu.onToggle(option.id)}
                                                >
                                                    <CheckboxSquare
                                                        isChecked={option.isVisible}
                                                        isHovered={false}
                                                        isFocused={false}
                                                        disabled={!!option.disabled}
                                                        styles={{
                                                            width: '2rem',
                                                            height: '2rem',
                                                            minWidth: '2rem'
                                                        }}
                                                    />
                                                    <P noDefaultMargin>{option.label}</P>
                                                </ClickableOpacity>
                                            ))}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </Section>
                )}
            </div>
        </div>
    ) : null;
};

export default LimitItems;
